import { ClockCounterClockwise } from "@phosphor-icons/react";
import { useState } from "react";

import { AUDIT_PAGE_SIZE, saveAuditExport, useAuditExport, useAuditHistory } from "../api/audit";
import type { AuditEvent } from "../api/audit";
import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import { PersonName } from "./PersonRef";
import { Loaded, WriteFailed } from "../components/Loaded";
import { Panel } from "../components/Panel";
import { ActionButton, PageHeaderCard, Tile, TileStrip } from "../components/ui";
import { reasonWords, securityEventWords } from "../lib/customerWords";
import { shortDate, zoned } from "../lib/format";
import type { Reader } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import own from "./Audit.module.css";
import { useScreen } from "./useScreen";

/** An audit event's moment, **with seconds**: a trail that cannot order two events a minute apart is
 * weak. In the reader's zone and labelled with it; UTC, labelled UTC, while their preferences are
 * not known. */
function eventMoment(iso: string, reader: Reader | undefined): string {
  const parts = zoned(iso, reader, true);
  return parts ? `${shortDate(parts.date)} ${parts.time} ${parts.zone}` : iso;
}

/** One event. **The reader is read here and not in `AuditBody`**, so the preferences are asked for
 * only once there is an event to place: a screen that read them on mount would make a request of
 * its own for a workspace with nothing in it, and for a role that is shown a sentence instead of
 * the history. */
function EventItem({ event }: { event: AuditEvent }) {
  const reader = useReader();
  return (
    <tr className={own.event} data-testid="audit-event" data-event-id={event.id}>
      <td className={own.time}>
        <time data-testid="audit-event-time" dateTime={event.created_at}>{eventMoment(event.created_at, reader)}</time>
      </td>
      <td className={own.type} data-testid="audit-event-type" data-event-type={event.event_type} title={event.event_type}>{securityEventWords(event.event_type)}</td>
      <td className={own.actor} data-testid="audit-event-actor">{event.actor_id ? <PersonName userId={event.actor_id} fallback={event.actor_id} /> : "Actor not recorded"}</td>
      <td className={own.reason} data-testid="audit-event-reason">{event.reason ? reasonWords(event.reason) : "Reason withheld or not recorded"}</td>
    </tr>
  );
}

/** `embedded` is the Admin log tab: the same content in the Admin place's own frame, with the export
 * button in the content instead of a page header that the place already draws. */
function AuditBody({ embedded = false }: { embedded?: boolean }) {
  const screen = useScreen();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const [offset, setOffset] = useState(0);
  const history = useAuditHistory(isAdmin, offset);
  const exported = useAuditExport();
  const [saveFailed, setSaveFailed] = useState(false);

  async function download() {
    setSaveFailed(false);
    try {
      const blob = await exported.mutateAsync();
      saveAuditExport(blob);
    } catch {
      setSaveFailed(true);
    }
  }

  const exportButton = isAdmin ? (
    <ActionButton disabled={exported.isPending} onClick={() => void download()}>
      {exported.isPending ? "Generating CSV…" : "Export security CSV"}
    </ActionButton>
  ) : undefined;
  // A failed refresh keeps the last page in the cache; it is not shown as known history.
  const page = history.error ? undefined : history.data;
  const kinds = page ? new Set(page.events.map((event) => event.event_type)).size : 0;

  const content = (
    <>
      {!isAdmin ? (
        <Panel title="Sign-ins and role changes" column="1 / 13">
          <p className={styles.note}>An Admin can read this workspace’s security history.</p>
        </Panel>
      ) : (
        <div className={own.stack}>
          <WriteFailed error={exported.error} of="Security export" />
          {saveFailed && !exported.error ? <p role="alert">The file could not be saved.</p> : null}
          {exported.isSuccess && !saveFailed ? <p role="status">CSV generated; saving was requested.</p> : null}

          {page ? (
            <div data-testid="audit-facts">
              <TileStrip label="This page of the history">
                <Tile kpi="audit-events" label="Events on this page" value={String(page.events.length)} />
                <Tile
                  kpi="audit-kinds"
                  label="Kinds of event"
                  value={String(kinds)}
                  link={<span className={own.tileNote}>Distinct on this page</span>}
                />
                <Tile
                  kpi="audit-older"
                  label="Older events"
                  value={page.has_more ? "More to read" : "None older"}
                  link={<span className={own.tileNote}>{offset === 0 ? "From the newest" : `Skipping ${offset}`}</span>}
                />
              </TileStrip>
            </div>
          ) : null}

          <Panel title="Sign-ins and role changes" sub="Each row: when, what, who did it, and why it was recorded">
            <Loaded query={history} of="Security history">
              {(loaded) => (
                <>
                  {loaded.events.length === 0 ? (
                    <p data-testid="audit-empty">No workspace-attributed security events.</p>
                  ) : (
                    <table className={own.events} data-testid="audit-events" aria-label="Sign-ins and role changes">
                      <thead>
                        <tr>
                          <th scope="col">When</th>
                          <th scope="col">What</th>
                          <th scope="col">Who</th>
                          <th scope="col">Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loaded.events.map((event) => (
                          <EventItem key={event.id} event={event} />
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className={own.paging}>
                    <ActionButton disabled={offset === 0} onClick={() => setOffset(offset - AUDIT_PAGE_SIZE)}>
                      Newer events
                    </ActionButton>
                    <ActionButton disabled={!loaded.has_more} onClick={() => setOffset(offset + AUDIT_PAGE_SIZE)}>
                      Older events
                    </ActionButton>
                  </div>
                </>
              )}
            </Loaded>
          </Panel>

          {/* **These stay visible paragraphs.** What this history leaves out is the half of it a
              reader needs to judge it, and the browser gate reads both sentences by their text, so
              they are not behind a closed control. They are ranked: a panel of their own, below the
              events, in quiet type. */}
          <Panel title="Basis and limits" sub="What this history leaves out">
            <p className={own.attribution} data-testid="audit-limits">
              Events without workspace attribution are excluded. Person details, address digests
              and IP prefixes are withheld. Export generation is recorded; receipt or saving is
              not confirmed. Document downloads elsewhere are not recorded by this feature.
            </p>
            {page ? (
              <p className={own.attribution} data-testid="audit-attribution-note">{page.attribution_note}</p>
            ) : null}
          </Panel>
        </div>
      )}
    </>
  );
  if (embedded) {
    return (
      <>
        {exportButton ? <div className={own.stack}>{exportButton}</div> : null}
        {content}
      </>
    );
  }
  return (
    <ScreenFrame screen={screen} title="Security history" crumb="workspace-attributed events" bodyScrolls>
      <PageHeaderCard
        icon={ClockCounterClockwise}
        title="Security history"
        label="Who signed in and which roles changed, newest first"
        actions={exportButton}
      />
      {content}
    </ScreenFrame>
  );
}

export function AuditContent() {
  return <AuditBody embedded />;
}

export function AuditScreen() {
  return <RequireSession><AuditBody /></RequireSession>;
}
